// Customer portal — shared PWA install helpers.
//
// One module that both the first-visit banner and the Account section read
// from. The browser's `beforeinstallprompt` event fires once, early in the
// page's life — so we capture it here (via initPwa(), called from main.ts
// before any consumer mounts) and hold onto the deferred prompt. Whichever
// surface the customer reaches later — the banner or Account & settings — can
// then offer a working "Add to home screen" button.
//
// iOS Safari never fires `beforeinstallprompt`; there installation is manual
// (Share → Add to Home Screen), so consumers detect the platform and show
// written steps instead of a button.
//
// This is install/UI plumbing only — no push subscription, no backend. Real
// Web Push (VAPID keys, subscription storage, server-side send) is a separate
// build; see the "Push notifications — coming soon" block in the Account
// section.

export type PwaPlatform = "ios" | "android" | "desktop";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
let started = false;
const availListeners = new Set<() => void>();
const installedListeners = new Set<() => void>();

/** Register the global install listeners. Call once at startup, before any
 *  consumer mounts. Idempotent — safe to call more than once. */
export function initPwa(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  window.addEventListener("beforeinstallprompt", (e) => {
    // Stop Chrome's mini-infobar so we control when/where the prompt appears.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    availListeners.forEach((cb) => {
      try { cb(); } catch { /* ignore */ }
    });
  });

  window.addEventListener("appinstalled", () => {
    installed = true;
    deferredPrompt = null;
    installedListeners.forEach((cb) => {
      try { cb(); } catch { /* ignore */ }
    });
  });
}

/** Best-effort platform bucket for choosing install guidance. */
export function detectPlatform(): PwaPlatform {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  const iOS =
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ reports as desktop Safari but is touch-capable.
    ((navigator.platform === "MacIntel" || /Macintosh/i.test(ua)) &&
      (navigator.maxTouchPoints ?? 0) > 1);
  if (iOS) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

/** True when already running as an installed/standalone app — nothing to offer. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/** iOS Safari is the only iOS browser whose Share sheet reliably offers "Add
 *  to Home Screen". Detect the common third-party / in-app browsers so we can
 *  nudge the customer to open the page in Safari. */
export function isIosSafari(): boolean {
  if (detectPlatform() !== "ios") return false;
  const ua = navigator.userAgent || "";
  return !/CriOS|FxiOS|EdgiOS|OPiOS|mercury|GSA|FBAN|FBAV|Instagram|Line|DuckDuckGo/i.test(ua);
}

/** Whether a native install prompt is currently available (Android/desktop). */
export function canPromptInstall(): boolean {
  return deferredPrompt != null;
}

/** Whether the app has been installed this session (via prompt or browser menu). */
export function isInstalled(): boolean {
  return installed;
}

/** Fire the native install prompt. Resolves to the user's choice, or
 *  "unavailable" when there's no deferred prompt to show. A deferred prompt
 *  can only be consumed once. */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredPrompt) return "unavailable";
  const evt = deferredPrompt;
  deferredPrompt = null;
  try {
    await evt.prompt();
    const choice = await evt.userChoice;
    return choice.outcome;
  } catch {
    return "dismissed";
  }
}

/** Subscribe to "a native install prompt just became available". Returns an
 *  unsubscribe function. */
export function onInstallAvailable(cb: () => void): () => void {
  availListeners.add(cb);
  return () => { availListeners.delete(cb); };
}

/** Subscribe to "the app was installed". Returns an unsubscribe function. */
export function onInstalled(cb: () => void): () => void {
  installedListeners.add(cb);
  return () => { installedListeners.delete(cb); };
}
